import { ChangeEvent, FormEvent, MouseEvent, useState } from "react";

import {
  useInput,
  useApisMembersBusinessSectionsEmployeeConditionSurveysIndex,
  useArray,
  useProvidersEmployeeLayout,
} from "~/hooks";

import {
  ApiMembersBusinessSectionEmployeeConditionSurveysIndexRequestType,
  EmployeeConditionSurveyType,
  EmployeeRole,
} from "~/domains";

type ReturnType = {
  managersEmployeeConditionSurveys: EmployeeConditionSurveyType[];
  ownersEmployeeConditionSurveys: EmployeeConditionSurveyType[];
  keyword: string;
  managerHasNextPage?: boolean;
  managerIsLoading: boolean;
  ownerIsLoading: boolean;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  managerFetchNextPage: () => void;
  ownerFetchNextPage: () => void;
  ownerHasNextPage?: boolean;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
};

type PropsType = {
  businessSectionId?: string;
};

export const useSearchMembersBusinessSectionsEmployeeConditionSurveys = ({
  businessSectionId = "",
}: PropsType): ReturnType => {
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const { items: managerItems, setItems: setManagerItems } =
    useArray<EmployeeConditionSurveyType>([]);
  const { items: ownerItems, setItems: setOwnerItems } =
    useArray<EmployeeConditionSurveyType>([]);
  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");

  const [params, setParams] =
    useState<ApiMembersBusinessSectionEmployeeConditionSurveysIndexRequestType>();

  const {
    isFetching: isFetchingManager,
    fetchNextPage: fetchNextPageManager,
    hasNextPage: hasNextPageManager,
  } = useApisMembersBusinessSectionsEmployeeConditionSurveysIndex({
    businessSectionId,
    params: { employeeRoleId: EmployeeRole.MANAGER.id, ...params },
    config: {
      enabled: !!businessSectionId,
      onSuccess: (data) => {
        setManagerItems(
          data.pages.map((page) => page.employeeConditionSurveys).flat(),
        );
      },
    },
  });

  const {
    isFetching: isFetchingOwner,
    fetchNextPage: fetchNextPageOwner,
    hasNextPage: hasNextPageOwner,
  } = useApisMembersBusinessSectionsEmployeeConditionSurveysIndex({
    businessSectionId,
    params: { employeeRoleId: EmployeeRole.OWNER.id, ...params },
    config: {
      enabled: !!businessSectionId,
      onSuccess: (data) => {
        setOwnerItems(
          data.pages.map((page) => page.employeeConditionSurveys).flat(),
        );
      },
    },
  });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    setParams(undefined);
    setFalseRightSidebarMain();
  };

  return {
    managersEmployeeConditionSurveys: managerItems,
    ownersEmployeeConditionSurveys: ownerItems,
    keyword,
    managerHasNextPage: hasNextPageManager,
    managerIsLoading: isFetchingManager,
    ownerIsLoading: isFetchingOwner,
    ownerHasNextPage: hasNextPageOwner,
    managerFetchNextPage: fetchNextPageManager,
    ownerFetchNextPage: fetchNextPageOwner,
    onChangeKeyword,
    onSearchSubmit,
    onConditionReset,
  };
};
